import useGetApi from 'src/api/useGetApi';

interface RebateAgreementInfo {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

interface DuplicateDocumentInfo {
  documentId: string;
  originalFileName: string;
  fileSizeBytes: number;
  uploadedAt: string;
  uploadedBy: string | null;
  uploadedByUserId: string | null;
  rebateAgreements: RebateAgreementInfo[];
}

interface CheckDuplicatesResponse {
  hasDuplicates: boolean;
  duplicates: DuplicateDocumentInfo[];
}

type DocumentAssociationFilter =
  | 'rebate_invoice_agreement'
  | 'direct_rebate_agreement'
  | 'none';

interface UseCheckDuplicateDocumentsParams {
  originalFileName: string;
  fileSizeBytes: number;
  tolerancePercent?: number;
  associationFilter?: DocumentAssociationFilter;
  enabled?: boolean;
}

/**
 * Hook to check if a document with the same filename and similar file size
 * has been previously uploaded.
 *
 * Uses a tolerance percentage to account for minor file size variations
 * (e.g., line ending differences, metadata changes).
 *
 * @param params - Query parameters
 * @param params.originalFileName - The original filename to check (e.g., "vendor_report.csv")
 * @param params.fileSizeBytes - The file size in bytes
 * @param params.tolerancePercent - Percentage tolerance for file size matching (default: 1%)
 * @param params.associationFilter - Filter by association pattern (default: 'rebate_invoice_agreement')
 * @param params.enabled - Whether to enable the query (default: true)
 */
export function useCheckDuplicateDocuments({
  originalFileName,
  fileSizeBytes,
  tolerancePercent = 1,
  associationFilter = 'rebate_invoice_agreement',
  enabled = true,
}: UseCheckDuplicateDocumentsParams) {
  return useGetApi<CheckDuplicatesResponse>({
    endpoint: 'core/documents/check-duplicates',
    queryKey: [
      'checkDuplicateDocuments',
      originalFileName,
      fileSizeBytes,
      tolerancePercent,
      associationFilter,
    ],
    queryParameters: {
      originalFileName,
      fileSizeBytes,
      tolerancePercent,
      associationFilter,
    },
    options: {
      enabled: enabled && Boolean(originalFileName) && fileSizeBytes > 0,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
    },
  });
}

export type {
  DuplicateDocumentInfo,
  CheckDuplicatesResponse,
  RebateAgreementInfo,
};
